import { encode, type MarketAvailableMarkets, type WalletCurrency } from '@capsicum/common';
import { ListCard } from '@capsicum/common-web';
import { useWalletFormatter } from '@capsicum/socios-core';
import { MarketFanTokenBuySellButtons } from '@capsicum/socios-core-web';
import { EvolutionPercentage } from 'libs/common-web/ui/atoms';
import { memo } from 'react';
import styled from 'styled-components';

export type TrendingFanTokenCardProps = {
	amount: string;
	cardPosition: number;
	currency?: WalletCurrency;
	isBuyEnabled: boolean;
	isBuyLoading: boolean;
	listLength: number;
	market: MarketAvailableMarkets;
	onClickBuy: (fanTokenCurrencyId?: string) => void;
	onClickLogo: (partnerId?: string) => void;
	partnerId?: string;
	partnerLogo?: string;
	partnerShortName?: string;
	testID: string;
};

const TrendingFanTokenCardComponent = ({
	amount,
	cardPosition,
	currency,
	isBuyEnabled,
	isBuyLoading,
	listLength,
	market,
	onClickBuy,
	onClickLogo,
	partnerId,
	partnerLogo,
	partnerShortName,
	testID
}: TrendingFanTokenCardProps) => {
	const { formatEvolutionPercentage, formatPrice } = useWalletFormatter();

	const formattedAmount = formatPrice({ amount, currency });

	return (
		<S.ListCardContainer $cardPosition={cardPosition} $listLength={listLength} testID={testID}>
			<ListCard.Start $lessPadding>
				<ListCard.Image optimized src={partnerLogo} testID={testID} onClick={() => onClickLogo(partnerId)} />
			</ListCard.Start>

			<ListCard.Content $noGap>
				<ListCard.Row>
					<ListCard.Title>{partnerShortName}</ListCard.Title>
				</ListCard.Row>
				<ListCard.Row>
					<S.Subtitle>
						<S.BaseCurrencySpan>{`1 ${market.baseCurrency?.shortCode} `}</S.BaseCurrencySpan>
						<S.AmountSpan>{`${encode('doubleTilde')}${formattedAmount}`}</S.AmountSpan>
						&nbsp;
						<EvolutionPercentage
							isPercentagePositive
							marketEvolutionPercentage={formatEvolutionPercentage(market.ticker?.evolutionPercentage)}
						/>
					</S.Subtitle>
				</ListCard.Row>
			</ListCard.Content>
			<S.ListCardEnd>
				<MarketFanTokenBuySellButtons
					fanTokenCurrencyId={market?.baseCurrency?.id}
					isBuyEnabled={isBuyEnabled}
					isBuyLoading={isBuyLoading}
					isMarketAvailable={!!market}
					isSellEnabled={false}
					isSellLoading={false}
					partnerId={partnerId}
					showSell={false}
					testID={testID}
					onClickBuy={onClickBuy}
				/>
			</S.ListCardEnd>
		</S.ListCardContainer>
	);
};

export const TrendingFanTokenCard = memo(TrendingFanTokenCardComponent, (prevProps, nextProps) => {
	// This is to prevent re-rendering of the whole list when buy or sell is clicked and only re-render the clicked market
	return prevProps.isBuyLoading === nextProps.isBuyLoading;
});

namespace S {
	export const ListCardContainer = styled(ListCard)<{ $cardPosition: number; $listLength: number }>`
		border-top-left-radius: ${({ $cardPosition }) => ($cardPosition === 0 ? 'auto' : '0px')};
		border-top-right-radius: ${({ $cardPosition }) => ($cardPosition === 0 ? 'auto' : '0px')};
		border-bottom-left-radius: ${({ $cardPosition, $listLength }) =>
			$cardPosition === $listLength ? 'auto' : '0px'};
		border-bottom-right-radius: ${({ $cardPosition, $listLength }) =>
			$cardPosition === $listLength ? 'auto' : '0px'};

		overflow: auto;
		gap: 0 !important;
	`;

	export const ListCardEnd = styled(ListCard.End)`
		margin-left: 0;
		padding-left: 0;
	`;

	export const BaseCurrencySpan = styled.span`
		${({ theme }) => theme.fonts.text.caption2}
	`;
	export const AmountSpan = styled.span`
		${({ theme }) => theme.fonts.text.caption1}
	`;
	export const Subtitle = styled(ListCard.SubTitle)`
		display: block;
		justify-content: center;
		align-items: center;
	`;
}
